import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Star, Check, Truck, ShieldCheck, RotateCcw, Mail, Maximize2, Clock, Wrench, Home } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { getProducts } from '../lib/api';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import useSEO from '../hooks/useSEO';

const HomePage = () => {
  useSEO({ title: null, description: 'NestFlow — Premium space-saving storage solutions. Shop sliding organizers, stackable bins, cable clips and more.' });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroProduct, setHeroProduct] = useState(null);
  const navigate = useNavigate();
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProducts();
        const inStock = data.filter(p => p.in_stock);
        setProducts(inStock);
        const hero = inStock.find(p => p.name.toLowerCase().includes('2-tier'));
        setHeroProduct(hero || inStock[0]);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Header />

      {/* ============ HERO ============ */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Copy */}
            <div className="opacity-0 animate-fade-in-up">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#1A1A1C] font-['Manrope'] tracking-tighter leading-[0.92] mb-6">
                Maximize Your<br />Space Instantly
              </h1>
              <p className="text-lg text-[#555] leading-relaxed mb-8 max-w-lg">
                Turn messy cabinets into clean, organized storage in minutes.
              </p>

              <button
                onClick={() => navigate('/products')}
                className="bg-[#1A1A1C] text-white px-10 py-4 rounded-full text-base font-semibold inline-flex items-center gap-2.5 hover:bg-[#333] transition-all hover:-translate-y-0.5 shadow-sm"
                data-testid="hero-cta-primary"
              >
                Shop Storage Solutions
                <ArrowRight className="w-5 h-5" strokeWidth={2} />
              </button>

              <p className="mt-5 text-sm text-[#888]">
                Simple solutions for everyday organization
              </p>

              {/* Social proof */}
              <div className="flex items-center gap-4 mt-10 pt-8 border-t border-[#EBEBEB]">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#1A1A1C] text-[#1A1A1C]" />
                  ))}
                </div>
                <p className="text-sm text-[#5C5C60]">
                  Trusted by <span className="font-semibold text-[#1A1A1C]">2,500+</span> happy customers
                </p>
              </div>
            </div>

            {/* Hero image */}
            <div className="relative opacity-0 animate-fade-in-up animation-delay-200">
              <div className="aspect-[4/3] lg:aspect-square rounded-2xl overflow-hidden bg-[#F5F5F5]">
                <img
                  src="https://images.unsplash.com/photo-1724217600531-fdde7cad8651?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwd2hpdGUlMjBraXRjaGVuJTIwb3JnYW5pemVkJTIwc2hlbHZlcyUyMG1vZGVybiUyMGNsZWFufGVufDB8fHx8MTc3NzQxOTQzM3ww&ixlib=rb-4.1.0&q=85"
                  alt="Modern organized kitchen with clean white shelves"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Floating product overlay */}
              {heroProduct && (
                <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-[#F0F0F0]">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-lg bg-[#F5F5F5] overflow-hidden flex-shrink-0">
                      <img src={heroProduct.image_url} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1A1A1C] truncate">{heroProduct.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="font-bold text-[#1A1A1C]">${heroProduct.price}</span>
                        {heroProduct.original_price && (
                          <span className="text-sm text-[#999] line-through">${heroProduct.original_price}</span>
                        )}
                        <span className="text-xs text-[#888]">Best Seller</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST BAR ============ */}
      <section className="py-12 border-y border-[#F0F0F0]" data-testid="trust-bar">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-0 sm:divide-x divide-[#E8E8E8]">
            {[
              { icon: <Truck className="w-6 h-6" strokeWidth={1.5} />, title: 'Fast Shipping', sub: 'Free on orders over $50' },
              { icon: <ShieldCheck className="w-6 h-6" strokeWidth={1.5} />, title: 'Secure Checkout', sub: 'Powered by Stripe' },
              { icon: <RotateCcw className="w-6 h-6" strokeWidth={1.5} />, title: 'Easy Returns', sub: '14-day return policy' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 sm:justify-center sm:px-6">
                <div className="w-12 h-12 rounded-full bg-[#F5F5F5] flex items-center justify-center text-[#1A1A1C]">
                  {item.icon}
                </div>
                <div>
                  <p className="font-semibold text-[#1A1A1C] text-sm">{item.title}</p>
                  <p className="text-xs text-[#888] mt-0.5">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section id="benefits" className="py-20 lg:py-24" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1C] font-['Manrope'] tracking-tight">
              Finally Fix Your Clutter For Good
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Maximize2 className="w-6 h-6" strokeWidth={1.5} />, title: 'Turn Clutter Into Organized Space', desc: 'Maximize every inch of your cabinets and drawers with smart vertical designs.' },
              { icon: <Clock className="w-6 h-6" strokeWidth={1.5} />, title: 'Set Up In Minutes — No Tools Needed', desc: 'Unbox, slide into place, and start organizing right away.' },
              { icon: <Wrench className="w-6 h-6" strokeWidth={1.5} />, title: 'Strong, Stable, Built For Daily Use', desc: 'Premium materials that hold up to everyday wear and tear.' },
              { icon: <Home className="w-6 h-6" strokeWidth={1.5} />, title: 'Fits Kitchens, Bathrooms, And More', desc: 'Versatile designs that work in any room of your home.' },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[#FAFAFA] rounded-xl p-7 hover:bg-[#F5F5F5] transition-colors opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="w-12 h-12 rounded-lg bg-white border border-[#EBEBEB] flex items-center justify-center text-[#1A1A1C] mb-5">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-[#1A1A1C] font-['Manrope'] mb-2 leading-snug">{item.title}</h3>
                <p className="text-sm text-[#777] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FEATURED PRODUCT ============ */}
      {heroProduct && (
        <section className="py-20 lg:py-24 bg-[#FAFAFA]" data-testid="featured-product-section">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Large product image */}
              <div className="aspect-square rounded-2xl overflow-hidden bg-[#F0F0F0]">
                <img
                  src={heroProduct.image_url}
                  alt={heroProduct.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Product details */}
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#888] mb-3 block">
                  Best Seller
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1C] font-['Manrope'] tracking-tight mb-4">
                  {heroProduct.name}
                </h2>
                <p className="text-[#555] leading-relaxed mb-6">
                  {heroProduct.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {heroProduct.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#444]">
                      <Check className="w-5 h-5 text-[#1A1A1C] flex-shrink-0" strokeWidth={2} />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Pricing */}
                <div className="flex items-center gap-3 mb-8">
                  <span className="text-4xl font-bold text-[#1A1A1C]">${heroProduct.price}</span>
                  {heroProduct.original_price && (
                    <span className="text-lg text-[#AAA] line-through">${heroProduct.original_price}</span>
                  )}
                  {heroProduct.original_price && (
                    <span className="bg-[#1A1A1C] text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Save ${(heroProduct.original_price - heroProduct.price).toFixed(0)}
                    </span>
                  )}
                </div>

                {/* CTA */}
                <button
                  onClick={() => handleAddToCart(heroProduct)}
                  className="bg-[#1A1A1C] text-white px-10 py-4 rounded-full text-base font-semibold inline-flex items-center gap-2.5 hover:bg-[#333] transition-all hover:-translate-y-0.5 shadow-sm"
                  data-testid="featured-product-cta"
                >
                  Get Organized Now
                  <ArrowRight className="w-5 h-5" strokeWidth={2} />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ SOCIAL PROOF ============ */}
      <section id="reviews" className="py-20 lg:py-24" data-testid="reviews-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1C] font-['Manrope'] tracking-tight">
              Why Customers Love It
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Sarah M.',
                text: 'Super easy to install and made a huge difference.',
              },
              {
                name: 'Alex K.',
                text: 'My cabinet has never looked this clean.',
              },
              {
                name: 'Maya L.',
                text: 'Simple idea but works perfectly.',
              },
            ].map((review, i) => (
              <div
                key={i}
                className="bg-[#FAFAFA] rounded-xl p-8 text-center opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${i * 100}ms` }}
                data-testid={`review-card-${i}`}
              >
                <div className="flex items-center justify-center gap-0.5 mb-5">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-[#1A1A1C] text-[#1A1A1C]" />
                  ))}
                </div>
                <p className="text-[#444] text-base leading-relaxed mb-5">"{review.text}"</p>
                <p className="text-sm font-semibold text-[#1A1A1C]">{review.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section className="py-16 bg-[#FAFAFA] border-t border-[#F0F0F0]" data-testid="contact-section">
        <div className="max-w-xl mx-auto px-6 text-center">
          <Mail className="w-8 h-8 text-[#1A1A1C] mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="text-2xl font-bold text-[#1A1A1C] font-['Manrope'] mb-2">
            Questions? We're here to help.
          </h2>
          <p className="text-[#888] mb-1">Reach us anytime at</p>
          <a
            href="mailto:nestflowstore@gmail.com"
            className="text-[#1A1A1C] font-semibold hover:underline"
            data-testid="contact-email"
          >
            nestflowstore@gmail.com
          </a>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default HomePage;
